import { useState, useMemo, useEffect } from 'react';
import '../../../assets/css/StudentDashboard.css';
import { getData, PostdataWithJson } from '../../../API/GlobalApi.js';
import Notifier from '../../../Utils/notify.js';
import AdvanceTable from '../../../Utils/AdvanceTables.js';
import TableSkeleton from '../../../Utils/TableSkeleton.js';
import { reactSelectStyles } from '../../../Utils/selectBoxStyle.js';
import Select from 'react-select';
import SpinnerLoader from '../../../Utils/Spinner.js';
import TableLoaderOverlay from '../../../Utils/tableFadingLoader.js';
import { useTableFade } from '../../../Utils/tableFadeHooks.js';
import { AdmitCardCompareModal } from '../../../Component/Modals/AdmitCardCompareModal.js';
import { PromoteFromAdmitCardModal } from '../../../Component/Modals/PromoteFromAdmitCardModal.js';

const ARCHIVE_URL = 'students/admit-card-archive/';
const FILTER_OPTIONS_URL = 'students/admit-card-archive/filter-options/';
const REMATCH_URL = 'students/admit-card-archive/rematch/';
const BACKFILL_URL = 'students/admit-card-archive/backfill-parents/';

const CARD_TYPE_OPTIONS = [
    { label: 'Regular', value: 'REGULAR' },
    { label: 'Back Paper', value: 'BACK_PAPER' },
];

const MATCH_STATUS_OPTIONS = [
    { label: 'Matched', value: 'matched' },
    { label: 'Not matched', value: 'unmatched' },
];

const PARENTS_STATUS_OPTIONS = [
    { label: 'Missing a name', value: 'missing' },
    { label: 'Both present', value: 'complete' },
];

const selectStyles = {
    ...reactSelectStyles,
    control: (base, state) => ({
        ...reactSelectStyles.control?.(base, state),
        minHeight: '34px',
    }),
};

const AdmitCardArchive = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [archiveData, setArchiveData] = useState([]);
    const [filterQueryString, setFilterQueryString] = useState({});
    const [filterLoading, setFilterLoading] = useState(false);
    const [summary, setSummary] = useState({ total: 0, matched: 0, unmatched: 0, missing_parents: 0 });

    // Full course -> specialization -> semester tree, fetched once.
    const [courseTree, setCourseTree] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);
    const [specializationOptions, setSpecializationOptions] = useState([]);
    const [semesterOptions, setSemesterOptions] = useState([]);

    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedSpecialization, setSelectedSpecialization] = useState(null);
    const [selectedSemester, setSelectedSemester] = useState(null);
    const [selectedCardType, setSelectedCardType] = useState(null);
    const [selectedMatchStatus, setSelectedMatchStatus] = useState(null);
    const [selectedParentsStatus, setSelectedParentsStatus] = useState(null);

    const [compareCardId, setCompareCardId] = useState(null);
    const [promoteModalOpen, setPromoteModalOpen] = useState(false);
    const [reprocessing, setReprocessing] = useState(false);
    const [backfilling, setBackfilling] = useState(false);

    const pageSize = 10;
    const pageCounts = Math.ceil(totalCount / pageSize);

    const { skipSpinnerRef, tableLoading, triggerTableLoading, stopTableLoading } = useTableFade();

    const getArchive = async (page = 0, query = filterQueryString) => {
        const params = new URLSearchParams(query || {});
        params.set('page', page + 1);

        if (!skipSpinnerRef.current && isInitialLoad) setIsLoading(true);

        try {
            const response = await getData(`${ARCHIVE_URL}?${params.toString()}`);
            setArchiveData(response?.results || []);
            setTotalCount(response?.count || 0);
            setSummary(response?.summary || { total: 0, matched: 0, unmatched: 0, missing_parents: 0 });
        } catch (error) {
            Notifier.error('Failed to load admit cards');
            setArchiveData([]);
            setTotalCount(0);
            setSummary({ total: 0, matched: 0, unmatched: 0, missing_parents: 0 });
        } finally {
            if (!skipSpinnerRef.current && isInitialLoad) {
                setIsLoading(false);
                setIsInitialLoad(false);
            }
            setFilterLoading(false);
            skipSpinnerRef.current = false;
        }
    };

    useEffect(() => {
        getArchive(currentPage).finally(() => stopTableLoading());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filterQueryString]);

    // ── Course / Specialization / Semester dependent dropdowns ──
    const getFilterOptions = async () => {
        try {
            const response = await getData(FILTER_OPTIONS_URL);
            const tree = response?.data || [];
            setCourseTree(tree);
            setCourseOptions(tree.map((c) => ({ label: c.name, value: c.id })));
        } catch (error) {
            setCourseTree([]);
            setCourseOptions([]);
        }
    };

    useEffect(() => {
        getFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = (changes) => {
        setCurrentPage(0);
        skipSpinnerRef.current = true;
        setFilterLoading(true);
        setFilterQueryString((prev) => ({ ...prev, ...changes }));
    };

    const handleCourseChange = (course) => {
        setSelectedCourse(course);
        setSelectedSpecialization(null);
        setSelectedSemester(null);
        setSemesterOptions([]);

        const specs = courseTree.find((c) => c.id === course?.value)?.specializations || [];
        setSpecializationOptions(
            specs.map((s) => ({ label: s.name, value: s.id, academic_groups: s.academic_groups || [] }))
        );

        applyFilters({
            course_id: course?.value || '',
            specialization_id: '',
            academic_group_id: '',
        });
    };

    const handleSpecializationChange = (spec) => {
        setSelectedSpecialization(spec);
        setSelectedSemester(null);
        setSemesterOptions((spec?.academic_groups || []).map((ag) => ({ label: ag.name, value: ag.id })));

        applyFilters({
            specialization_id: spec?.value || '',
            academic_group_id: '',
        });
    };

    const handleSemesterChange = (sem) => {
        setSelectedSemester(sem);
        applyFilters({ academic_group_id: sem?.value || '' });
    };

    const handleCardTypeChange = (type) => {
        setSelectedCardType(type);
        applyFilters({ card_type: type?.value || '' });
    };

    const handleMatchStatusChange = (status) => {
        setSelectedMatchStatus(status);
        applyFilters({ match_status: status?.value || '' });
    };

    const handleParentsStatusChange = (status) => {
        setSelectedParentsStatus(status);
        applyFilters({ parents_status: status?.value || '' });
    };

    const handlePageChange = ({ selected }) => {
        triggerTableLoading();
        setCurrentPage(selected);
    };

    const handleReprocess = async () => {
        setReprocessing(true);
        try {
            // Send the active filters, not row ids: the server re-matches every
            // unmatched row in the current view, not just the page on screen.
            const response = await PostdataWithJson(REMATCH_URL, filterQueryString);
            if (response?.error) {
                Notifier.error(response.error);
                return;
            }
            const queued = response?.queued || 0;
            if (!queued) {
                Notifier.warn(response?.message || 'No unmatched admit cards to reprocess.');
                return;
            }
            Notifier.success(`${response.message} Refreshing…`);
            // Matching runs on celery, so give the workers a moment before
            // reloading, then reload regardless of how far they have got.
            setTimeout(() => {
                skipSpinnerRef.current = true;
                getArchive(currentPage);
            }, 2500);
        } catch (error) {
            Notifier.error('Reprocessing failed');
        } finally {
            setReprocessing(false);
        }
    };

    const handleBackfill = async () => {
        setBackfilling(true);
        try {
            // Filters, not row ids — the server backfills every row in the
            // current view that is missing a parent name, not just this page.
            const response = await PostdataWithJson(BACKFILL_URL, filterQueryString);
            if (response?.error) {
                Notifier.error(response.error);
                return;
            }
            const queued = response?.queued || 0;
            if (!queued) {
                Notifier.warn(response?.message || 'No admit cards are missing a parent name.');
                return;
            }
            Notifier.success(`${response.message} Refreshing…`);
            setTimeout(() => {
                skipSpinnerRef.current = true;
                getArchive(currentPage);
            }, 2500);
        } catch (error) {
            Notifier.error('Name backfill failed');
        } finally {
            setBackfilling(false);
        }
    };

    const missingParentsCount = summary.missing_parents || 0;

    const formattedData = useMemo(() => {
        return (archiveData || []).map((card) => ({
            id: card.id,
            can_promote: card.can_promote,
            promotion_blocked_reason: card.promotion_blocked_reason,
            student_academic_group: card.student_academic_group,
            candidate_name: card.candidate_name,
            roll_number: card.roll_number,
            enroll_number: card.enroll_number,
            student_name: card.student_name,
            registration_number: card.permanent_registration_number,
            semester: card.academic_group,
            card_type_display: card.card_type_display,
            examination_year: card.examination_year,
            is_student_matched: card.is_student_matched,
            matching_confidence: card.matching_confidence,
            probable_match_name: card.probable_match_name,
            probable_matching_confidence: card.probable_matching_confidence,
        }));
    }, [archiveData]);

    const textCell = (value) => (
        <p className="no-text-cursor" style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}>
            {value || '-'}
        </p>
    );

    const columns = useMemo(
        () => [
            {
                Header: () => <p style={{ textAlign: 'left' }}>Candidate Name (on card)</p>,
                accessor: 'candidate_name',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.candidate_name),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Roll No.</p>,
                accessor: 'roll_number',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.roll_number),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Enrollment No.</p>,
                accessor: 'enroll_number',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.enroll_number),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Matched Student</p>,
                accessor: 'student_name',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const { is_student_matched, student_name, probable_match_name, probable_matching_confidence } =
                        row.original;
                    if (is_student_matched) return textCell(student_name);
                    if (probable_match_name) {
                        return (
                            <p className="no-text-cursor" style={{ textAlign: 'left', color: '#B98900', fontWeight: 500, margin: 0 }}>
                                {probable_match_name} (probable {probable_matching_confidence}%)
                            </p>
                        );
                    }
                    return textCell(null);
                },
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Registration No.</p>,
                accessor: 'registration_number',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.registration_number),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Semester</p>,
                accessor: 'semester',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.semester),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Type</p>,
                accessor: 'card_type_display',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.card_type_display),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Exam Year</p>,
                accessor: 'examination_year',
                disableSortBy: true,
                Cell: ({ row }) => textCell(row.original.examination_year),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Match</p>,
                accessor: 'is_student_matched',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <p
                        className="no-text-cursor"
                        style={{
                            textAlign: 'left',
                            margin: 0,
                            fontWeight: 600,
                            color: row.original.is_student_matched ? '#39886F' : '#C0392B',
                        }}
                    >
                        {row.original.is_student_matched ? `Matched (${row.original.matching_confidence}%)` : 'Not matched'}
                    </p>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Promotion</p>,
                accessor: 'can_promote',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const { can_promote, promotion_blocked_reason, student_academic_group, semester } = row.original;
                    if (can_promote) {
                        return (
                            <p
                                className="no-text-cursor"
                                style={{ textAlign: 'left', margin: 0, fontWeight: 600, color: '#1F6F55' }}
                            >
                                {student_academic_group} → {semester}
                            </p>
                        );
                    }
                    return (
                        <p
                            className="no-text-cursor"
                            title={promotion_blocked_reason || ''}
                            style={{ textAlign: 'left', margin: 0, color: '#9CA3AF', fontSize: '12px' }}
                        >
                            {promotion_blocked_reason || '—'}
                        </p>
                    );
                },
            },
        ],
        []
    );

    return (
        <>
            {isLoading ? (
                <TableSkeleton rows={10} columns={9} showHeader={true} />
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {/* ── Title ── */}
                    <div style={{ flexShrink: 0, padding: '0 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                            <h2 className="main-heading">Admit Cards ({totalCount})</h2>
                            <div style={{ display: 'flex', gap: '18px', alignItems: 'center', fontSize: '13px', fontWeight: 600 }}>
                                <span style={{ color: '#1F6F55' }}>Matched: {summary.matched}</span>
                                <span style={{ color: '#B0281B' }}>Not matched: {summary.unmatched}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Filters ── */}
                    <div style={{ flexShrink: 0, padding: '8px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                            <div style={{ width: '200px' }}>
                                <Select
                                    options={courseOptions}
                                    value={selectedCourse}
                                    onChange={handleCourseChange}
                                    placeholder="Select Course"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div style={{ width: '220px' }}>
                                <Select
                                    options={specializationOptions}
                                    value={selectedSpecialization}
                                    onChange={handleSpecializationChange}
                                    placeholder="Specialization"
                                    isDisabled={!selectedCourse}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div style={{ width: '180px' }}>
                                <Select
                                    options={semesterOptions}
                                    value={selectedSemester}
                                    onChange={handleSemesterChange}
                                    placeholder="Semester / Year"
                                    isDisabled={!selectedSpecialization}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div style={{ width: '160px' }}>
                                <Select
                                    options={CARD_TYPE_OPTIONS}
                                    value={selectedCardType}
                                    onChange={handleCardTypeChange}
                                    placeholder="Regular / Back"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div style={{ width: '150px' }}>
                                <Select
                                    options={MATCH_STATUS_OPTIONS}
                                    value={selectedMatchStatus}
                                    onChange={handleMatchStatusChange}
                                    placeholder="Match Status"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div style={{ width: '155px' }}>
                                <Select
                                    options={PARENTS_STATUS_OPTIONS}
                                    value={selectedParentsStatus}
                                    onChange={handleParentsStatusChange}
                                    placeholder="Parent Names"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <button
                                className="filter-btn"
                                disabled={!missingParentsCount || backfilling}
                                onClick={handleBackfill}
                                title={
                                    missingParentsCount
                                        ? `Read the father's / mother's name from the PDF text layer for ${missingParentsCount} card(s) missing one. No API call.`
                                        : 'No admit cards are missing a parent name'
                                }
                                style={{
                                    height: '35px',
                                    padding: '0 16px',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    background: 'white',
                                    border: `1px solid ${missingParentsCount && !backfilling ? '#7F56DA' : '#C7C9D1'}`,
                                    color: missingParentsCount && !backfilling ? '#7F56DA' : '#9CA3AF',
                                    cursor: missingParentsCount && !backfilling ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {backfilling ? 'Backfilling…' : `Backfill Names${missingParentsCount ? ` (${missingParentsCount})` : ''}`}
                            </button>
                            <button
                                className="filter-btn"
                                disabled={!summary.unmatched || reprocessing}
                                onClick={handleReprocess}
                                title={
                                    summary.unmatched
                                        ? `Re-run course-wide matching for ${summary.unmatched} unmatched admit card(s) in this view`
                                        : 'No unmatched admit cards to reprocess'
                                }
                                style={{
                                    height: '35px',
                                    padding: '0 16px',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    background: 'white',
                                    border: `1px solid ${summary.unmatched && !reprocessing ? '#7F56DA' : '#C7C9D1'}`,
                                    color: summary.unmatched && !reprocessing ? '#7F56DA' : '#9CA3AF',
                                    cursor: summary.unmatched && !reprocessing ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {reprocessing ? 'Reprocessing…' : `Reprocess Unmatched${summary.unmatched ? ` (${summary.unmatched})` : ''}`}
                            </button>
                            <button
                                className="filter-btn"
                                onClick={() => setPromoteModalOpen(true)}
                                title="Promote a whole class off their admit cards"
                                style={{
                                    height: '35px',
                                    padding: '0 16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    color: '#fff',
                                    background: '#7F56DA',
                                    cursor: 'pointer',
                                }}
                            >
                                Promote
                            </button>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            padding: '0 12px',
                            position: 'relative',
                        }}
                    >
                        {filterLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                <SpinnerLoader />
                            </div>
                        ) : formattedData?.length > 0 ? (
                            <>
                                <AdvanceTable
                                    columns={columns}
                                    pageCounts={pageCounts}
                                    handlePageChange={handlePageChange}
                                    data={formattedData}
                                    currentPage={currentPage}
                                    checkbox={false}
                                    handleRowClick={(rowId) => setCompareCardId(rowId)}
                                />
                                <TableLoaderOverlay isLoading={tableLoading} />
                            </>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 1,
                                    color: '#888',
                                    textAlign: 'center',
                                }}
                            >
                                No admit cards found.
                            </div>
                        )}
                    </div>

                    <PromoteFromAdmitCardModal
                        isOpen={promoteModalOpen}
                        onClose={() => setPromoteModalOpen(false)}
                        onPromoted={() => {
                            skipSpinnerRef.current = true;
                            getArchive(currentPage);
                        }}
                    />

                    <AdmitCardCompareModal
                        isOpen={!!compareCardId}
                        admitCardId={compareCardId}
                        onClose={() => setCompareCardId(null)}
                        onAssigned={() => {
                            skipSpinnerRef.current = true;
                            getArchive(currentPage);
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default AdmitCardArchive;
